import React from 'react';
import { Armchair, Bath, BedDouble, MapPin } from '../ui/icons';
import { PropertyItem } from '../../../../models';

interface PropertyCardProps {
  property: PropertyItem;
  onClick: (property: PropertyItem) => void;
  sourceName?: string;
  highlighted?: boolean;
}

const PropertyCard: React.FC<PropertyCardProps> = ({
  property,
  onClick,
  sourceName,
  highlighted = false,
}) => {
  const placeholderImage =
    'https://via.placeholder.com/300x200?text=No+Image';
  const mainImage =
    property.fields.photos && property.fields.photos.length > 0
      ? property.fields.photos[0]
      : placeholderImage;
  const addressParts = [
    property.fields.addressNameNumber,
    property.fields.addressStreet,
    property.fields.city,
    property.fields.postcode,
  ].filter(Boolean);
  const lat =
    typeof property.fields.latitude === 'number'
      ? property.fields.latitude
      : property.fields.latitude
      ? Number(property.fields.latitude)
      : undefined;
  const lng =
    typeof property.fields.longitude === 'number'
      ? property.fields.longitude
      : property.fields.longitude
      ? Number(property.fields.longitude)
      : undefined;
  const hasCoordinates =
    Number.isFinite(lat ?? NaN) && Number.isFinite(lng ?? NaN);
  const configuredLocation =
    typeof property.fields.location === 'string'
      ? property.fields.location.trim()
      : property.fields.location
      ? String(property.fields.location).trim()
      : '';
  const fallbackLocation = [property.fields.city, property.fields.state]
    .filter(Boolean)
    .join(', ');
  const locationDisplay =
    addressParts.length > 0
      ? addressParts.join(', ')
      : hasCoordinates
      ? `${lat}, ${lng}`
      : configuredLocation || fallbackLocation || 'Location not specified';

  const priceLabel = property.fields.price
    ? `${property.fields.currency || '£'}${property.fields.price.toLocaleString()}`
    : 'Price on application';

  return (
    <div
      data-property-highlight={property.id}
      className={`h-full flex flex-col bg-white rounded-lg shadow hover:shadow-lg transition-shadow cursor-pointer overflow-hidden ${
        highlighted ? 'ring-2 ring-primary-500' : ''
      }`}
    >
      <button
        onClick={() => onClick(property)}
        className="flex-grow flex flex-col items-stretch text-left"
      >
        <img
          src={mainImage}
          alt={property.fields.title}
          className="w-full h-48 object-cover"
        />
        <div className="flex-grow flex flex-col p-4">
          <div>
            <h6 className="text-lg font-semibold text-gray-900 mb-1 truncate">
              {property.fields.title}
            </h6>
            <p className="text-xs text-gray-500 mb-2">
              {sourceName || property.sourceId}
            </p>
          </div>

          <div
            className={`flex items-center text-sm mb-2 ${
              hasCoordinates ? 'text-green-600' : 'text-gray-600'
            }`}
            data-testid="property-location"
          >
            <MapPin
              className={`h-4 w-4 mr-1 ${
                hasCoordinates ? 'text-green-600' : 'text-gray-500'
              }`}
            />
            <span>{locationDisplay}</span>
          </div>

          <div className="flex items-center justify-between gap-4">
            <p className="text-lg font-semibold text-primary-600">
              {priceLabel}
            </p>
            <div className="flex items-center gap-3 text-sm text-gray-700">
              {property.fields.receptionRooms && (
                <div className="flex items-center">
                  <Armchair className="h-4 w-4 mr-1" />
                  <span>{property.fields.receptionRooms}</span>
                </div>
              )}
              {property.fields.bedrooms && (
                <div className="flex items-center">
                  <BedDouble className="h-4 w-4 mr-1" />
                  <span>{property.fields.bedrooms}</span>
                </div>
              )}
              {property.fields.bathrooms && (
                <div className="flex items-center">
                  <Bath className="h-4 w-4 mr-1" />
                  <span>{property.fields.bathrooms}</span>
                </div>
              )}
            </div>
          </div>
        </div>
      </button>
    </div>
  );
};

export default PropertyCard;
